import { createClient } from '@supabase/supabase-js'

// Environment variables - these will be replaced with actual values in production
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '__SUPABASE_URL__'
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '__SUPABASE_ANON_KEY__'

// Log environment configuration status in development
if (import.meta.env.DEV) {
  console.log('[Supabase] URL configured:', !supabaseUrl.includes('__'))
  console.log('[Supabase] Key configured:', !supabaseAnonKey.includes('__'))
}

/**
 * Convert technical error messages to user-friendly ones
 * @param {Error|string|object} error - The error to format
 * @returns {string} User-friendly error message
 */
export function formatErrorMessage(error) {
  const message = error?.message || error?.details || String(error)

  // Network errors
  if (message.includes('Failed to fetch') || message.includes('NetworkError') || message.includes('net::ERR')) {
    return 'Unable to connect. Please check your internet connection and try again.'
  }

  // Timeout errors
  if (message.includes('timeout') || message.includes('Timeout')) {
    return 'The request took too long. Please try again.'
  }

  // Authentication errors
  if (message.includes('JWT') || message.includes('token') || message.includes('unauthorized') || message.includes('Unauthorized')) {
    return 'Your session has expired. Please sign in again.'
  }

  // Permission errors
  if (message.includes('permission') || message.includes('denied') || message.includes('not allowed')) {
    return 'You don\'t have permission to do this.'
  }

  // Duplicate/unique constraint errors
  if (message.includes('unique') || message.includes('duplicate') || message.includes('already exists')) {
    return 'This already exists. Please try a different name.'
  }

  // Not found errors
  if (message.includes('not found') || message.includes('does not exist')) {
    return 'The requested item was not found.'
  }

  // Rate limiting
  if (message.includes('rate limit') || message.includes('too many requests')) {
    return 'Too many requests. Please wait a moment and try again.'
  }

  // Server errors
  if (message.includes('500') || message.includes('Internal Server Error')) {
    return 'Something went wrong on our end. Please try again later.'
  }

  // Database errors - hide technical details
  if (message.includes('PGRST') || message.includes('postgres') || message.includes('relation')) {
    return 'Something went wrong. Please try again.'
  }

  // If message is too technical (contains stack trace or code references), simplify it
  if (message.includes('.js:') || message.includes('at ') || message.length > 150) {
    return 'Something went wrong. Please try again.'
  }

  // Return the original message if it seems user-friendly enough
  return message
}

// Initialize Supabase client with optimal configuration
// Check for browser environment before accessing window.localStorage
const isBrowser = typeof window !== 'undefined'

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    ...(isBrowser && { storage: window.localStorage }),
  },
  realtime: {
    params: {
      eventsPerSecond: 10,
    },
  },
})

// Google OAuth Configuration
export const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID || '__GOOGLE_CLIENT_ID__'

// Helper function to check if environment is properly configured
export const isConfigured = () => {
  return !supabaseUrl.includes('__') && !supabaseAnonKey.includes('__')
}

// Authentication helpers
export const auth = {
  // Sign in with Google OAuth
  async signInWithGoogle() {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      })

      if (error) throw error
      return { data, error: null }
    } catch (error) {
      console.error('Google sign-in error:', error)
      return { data: null, error }
    }
  },

  // Sign out
  async signOut() {
    try {
      const { error } = await supabase.auth.signOut()
      if (error) throw error
      return { error: null }
    } catch (error) {
      console.error('Sign out error:', error)
      return { error }
    }
  },

  // Get current session
  async getSession() {
    try {
      const { data: { session }, error } = await supabase.auth.getSession()
      if (error) throw error
      return { session, error: null }
    } catch (error) {
      console.error('Get session error:', error)
      return { session: null, error }
    }
  },

  // Get current user
  async getUser() {
    try {
      const { data: { user }, error } = await supabase.auth.getUser()
      if (error) throw error
      return { user, error: null }
    } catch (error) {
      console.error('Get user error:', error)
      return { user: null, error }
    }
  },
}

// Database helpers
export const db = {
  // Profile operations
  profiles: {
    async get(userId) {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single()

      if (error) console.error('Error fetching profile:', error)
      return { data, error }
    },

    async create(profile) {
      const { data, error } = await supabase
        .from('profiles')
        .insert([profile])
        .select()
        .single()

      if (error) console.error('Error creating profile:', error)
      return { data, error }
    },

    async update(userId, updates) {
      const { data, error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', userId)
        .select()
        .single()

      if (error) console.error('Error updating profile:', error)
      return { data, error }
    },

    async verifyAge(userId) {
      return this.update(userId, {
        is_age_verified: true,
        consent_date: new Date().toISOString(),
      })
    },
  },

  // Space operations
  spaces: {
    async list(userId) {
      const { data, error } = await supabase
        .from('space_members')
        .select('space_id, spaces(*)')
        .eq('user_id', userId)

      if (error) console.error('Error fetching spaces:', error)
      return { data: data?.map(m => m.spaces).filter(Boolean) || [], error }
    },

    // Create space using RPC (handles UUID generation, member creation, default channels)
    async create(spaceName) {
      const { data, error } = await supabase.rpc('create_space_with_defaults', {
        space_name: spaceName
      })

      if (error) console.error('Error creating space:', error)
      return { data, error }
    },

    // Join space by invite code using RPC
    async joinByInvite(inviteCode) {
      const { data, error } = await supabase.rpc('join_space_by_invite', {
        invite_code_param: inviteCode
      })

      if (error) console.error('Error joining space:', error)
      return { data, error }
    },

    // Update space profile using RPC
    async update(spaceId, updates) {
      const { data, error } = await supabase.rpc('update_space_profile', {
        p_space_id: spaceId,
        p_name: updates.name || null,
        p_description: updates.description || null,
        p_avatar_url: updates.avatar_url || null,
        p_banner_url: updates.banner_url || null,
        p_accent_color: updates.accent_color || null
      })

      if (error) console.error('Error updating space:', error)
      return { data, error }
    },

    // Get space details using RPC
    async getDetails(spaceId) {
      const { data, error } = await supabase.rpc('get_space_details', {
        p_space_id: spaceId
      })

      if (error) console.error('Error fetching space details:', error)
      return { data, error }
    },
  },

  // Channel operations
  channels: {
    async list(spaceId) {
      const { data, error } = await supabase
        .from('channels')
        .select('*')
        .eq('space_id', spaceId)
        .order('sort_order')

      if (error) console.error('Error fetching channels:', error)
      return { data, error }
    },

    // Create channel using RPC
    async create(spaceId, name, description = '', type = 'text') {
      const { data, error } = await supabase.rpc('create_channel', {
        p_space_id: spaceId,
        p_name: name,
        p_description: description,
        p_type: type
      })

      if (error) console.error('Error creating channel:', error)
      return { data, error }
    },

    // Update channel using RPC
    async update(channelId, name) {
      const { data, error } = await supabase.rpc('update_channel', {
        p_channel_id: channelId,
        p_name: name
      })

      if (error) console.error('Error updating channel:', error)
      return { data, error }
    },

    // Delete channel using RPC
    async delete(channelId) {
      const { data, error } = await supabase.rpc('delete_channel', {
        p_channel_id: channelId
      })

      if (error) console.error('Error deleting channel:', error)
      return { data, error }
    },

    // Reorder channels using RPC
    async reorder(channelIds) {
      const { data, error } = await supabase.rpc('reorder_channels', {
        p_channel_ids: channelIds
      })

      if (error) console.error('Error reordering channels:', error)
      return { data, error }
    },
  },

  // Permission helpers
  permissions: {
    // Check if user has a specific permission in a space
    async check(spaceId, userId, permission) {
      const { data, error } = await supabase.rpc('member_has_permission', {
        p_space_id: spaceId,
        p_user_id: userId,
        p_permission: permission
      })

      if (error) console.error('Error checking permission:', error)
      return { data, error }
    },

    // Get member's highest role in a space
    async getHighestRole(spaceId, userId) {
      const { data, error } = await supabase.rpc('get_member_highest_role', {
        p_space_id: spaceId,
        p_user_id: userId
      })

      if (error) console.error('Error fetching highest role:', error)
      return { data, error }
    },
  },

  // Channel message operations (for space channels)
  messages: {
    async list(channelId, limit = 50) {
      const { data, error } = await supabase
        .from('channel_messages')
        .select('*, profiles:user_id(*)')
        .eq('channel_id', channelId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .limit(limit)

      if (error) console.error('Error fetching channel messages:', error)
      return { data: data?.reverse() || [], error }
    },

    async send(message) {
      const { data, error } = await supabase
        .from('channel_messages')
        .insert([message])
        .select('*, profiles:user_id(*)')
        .single()

      if (error) console.error('Error sending channel message:', error)
      return { data, error }
    },

    async delete(messageId) {
      const { data, error } = await supabase
        .from('channel_messages')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', messageId)
        .select()
        .single()

      if (error) console.error('Error deleting channel message:', error)
      return { data, error }
    },
  },

  // Friend operations
  friendships: {
    async list(userId) {
      const { data, error } = await supabase
        .from('friendships')
        .select('*')
        .or(`user_id.eq.${userId},friend_id.eq.${userId}`)
        .eq('status', 'accepted')

      if (error) console.error('Error fetching friends:', error)
      return { data, error }
    },

    async sendRequest(userFriendCode, currentUserId) {
      // First, find the user by friend_code
      const { data: targetUser, error: findError } = await supabase
        .from('profiles')
        .select('id')
        .eq('friend_code', userFriendCode)
        .single()

      if (findError || !targetUser) {
        console.error('Error finding user:', findError)
        return { data: null, error: findError || new Error('User not found') }
      }

      // Create friendship request
      const { data, error } = await supabase
        .from('friendships')
        .insert([{
          user_id_1: currentUserId,
          user_id_2: targetUser.id,
          status: 'pending',
        }])
        .select()
        .single()

      if (error) console.error('Error sending friend request:', error)
      return { data, error }
    },
  },
}

// Real-time subscriptions
export const realtime = {
  // Subscribe to new messages in a channel
  subscribeToMessages(channelId, callback) {
    return supabase
      .channel(`messages:${channelId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `channel_id=eq.${channelId}`,
        },
        callback
      )
      .subscribe()
  },

  // Subscribe to presence updates
  subscribeToPresence(userIds, callback) {
    return supabase
      .channel('presence')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `id=in.(${userIds.join(',')})`,
        },
        callback
      )
      .subscribe()
  },

  // Unsubscribe from a channel
  unsubscribe(subscription) {
    supabase.removeChannel(subscription)
  },
}
